import { useState } from 'react';
import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Link,
  IconButton,
  Paper,
  useMediaQuery
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile'; // ファイル添付アイコン
import DeleteIcon from '@mui/icons-material/Delete'; // 削除アイコン
import SortIcon from '@mui/icons-material/Sort'; // 並び替えアイコン
import theme from '../../../style/theme';
import { ApplicationData } from ".././ApplicationData";
import { useUser } from '../../../context/UserContext'; // useUser をインポート
import type { CommentList } from ".././ApplicationData";
import { axiosRequest } from '../../../service/AxiosService';
import { Download } from '@mui/icons-material';
import { ResizableBox } from 'react-resizable';
interface CommentProps {
  applicationData: ApplicationData;
}
interface CommentFile {
  success: boolean;
  id: string;
  name: string;

}
/**
 * コメント入力・履歴表示コンポーネント
 * - テキストコメントとファイルを添付して申請にコメントを追加できる
 * - コメント履歴を昇順/降順に切り替えて表示し、添付ファイルのダウンロードも可能
 * - props: applicationData (申請データ)
 */
const Comment: React.FC<CommentProps> = ({
  applicationData }) => {
  const [commentText, setCommentText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [commentHistory, setCommentHistory] = useState<CommentList[]>(
    Array.isArray(applicationData.commentList) ? applicationData.commentList : []
  );

  const [isSortedAscending, setIsSortedAscending] = useState(false);
  // コメントテキストの変更を反映する
  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(event.target.value);
  };

  // 添付ファイルの選択を反映する
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    const selectedFile = event.target.files ? event.target.files[0] : null;
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleFileDownload = async (fileId: string, fileName: string) => {
    //ダウンロード処理
    try {
      const blob = await axiosRequest<Blob>(
        "/download/file",
        "POST",
        {
          params: { fileId },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("ファイルダウンロードエラー:", error);
    } finally {
    }
  };

  // 選択中の添付ファイルを削除する
  const handleFileDelete = () => {
    setFile(null);
  };

  // コメントとファイルをサーバーに送信してコメントを追加する
  const handleAddComment = async () => {

    if ((commentText.trim() !== '' && file) || file || (commentText.trim() !== '' && !file)) {

      // 引数を設定する
      const formData = new FormData();
      formData.append('applicationEntryId', applicationData.applicationEntryId ?? "");
      formData.append('applicationNumber', applicationData.number ?? "");
      formData.append('value', commentText ?? "");
      formData.append('userId', user!.id ?? "");
      if (file) {
        formData.append('fileList', file ?? []);
      }
      const options = {
        data: formData
      };

      const data = await axiosRequest<CommentFile>(
        "/postComment/saveNewData",
        "POST",
        options
      );
      if (data.success) {
        const newComment = {
          value: commentText,
          user: user!.lastName + ' ' + user!.firstName,
          date: new Date().toLocaleString(),
          fileName: data.name,
          fileId: data.id
        };
        setCommentHistory([newComment, ...commentHistory]);
        setCommentText('');
        setFile(null);
      }
    }

  };


  // コメント履歴の昇順/降順を切り替える
  const handleSortChange = () => {
    setIsSortedAscending(!isSortedAscending);
  };


  const sortedComments = isSortedAscending
    ? [...commentHistory].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    : [...commentHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const isMobile = useMediaQuery("(max-width:600px)"); // 600px以下の場合はスマートフォンデバイス

  const { user } = useUser();  // useUser フックでユーザー情報を取得

  return (
    <Box component={Paper}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        mt: 2,
        mb: 10,
        overflow: "hidden",
        backgroundColor: theme.palette.secondary.main,

      }}>

      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>

          <Typography variant="h3" sx={{ mt: 1.3 }}>
            コメント
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%', gap: 2 }}>
          <TextField
            label="コメントを入力"
            value={commentText}
            onChange={handleCommentChange}
            variant="outlined"
            multiline
            rows={4}
            sx={{
              mb: 1,
              backgroundColor: '#FFFFFF',
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary' },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="ファイルを添付" arrow>
              <IconButton component="label">
                <AttachFileIcon />
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </IconButton>
            </Tooltip>
            {file && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Link
                  href={URL.createObjectURL(file)}
                  target="_blank"
                  sx={{
                    textDecoration: 'underline',
                    '&:hover': { cursor: 'pointer' },
                    ml: 1
                  }}
                >
                  {file.name}
                </Link>
                <IconButton
                  onClick={handleFileDelete}
                  sx={{ ml: 2 }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddComment}
            sx={{ mb: 2, width: isMobile ? "100%" : undefined }}
          >
            追加
          </Button>
        </Box>

        {/* 並び替えボタン */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            コメント履歴
          </Typography>
          <Button onClick={handleSortChange}>
            {isSortedAscending ? "コメント日時 降順" : "コメント日時 昇順"}
          </Button>
        </Box>

      <ResizableBox
        width={Infinity}             // 横幅を親に合わせる
        height={200}                 // 初期高さ
        axis="y"                     // 縦方向のみ
        minConstraints={[0, 100]}    // 最小高さ100px
        maxConstraints={[Infinity, 600]} // 最大高さ600px
        resizeHandles={['se']}
      >
        <Box

          sx={{
            width: '100%',
            height: '100%',
            minHeight: 100,
            borderRadius: '0px', // 角丸なし
            border: '2px solid #ccc', // グレーの枠線
            backgroundColor: theme.palette.secondary.main, // グレーの背景
            padding: '10px', // 内側の余白
            display: 'flex',
            flexDirection: 'column',
            overflowX: "hidden", // 内側でスクロール
            overflowY: "auto",
            resize: "vertical",
          }}
        >

          {/* 区切り線とスペース */}
          {/* <Box sx={{ borderBottom: '1px solid #333333', mb: 2 }} />  */}
          {/* コメントがない場合はプレースホルダーテキストを表示 */}
          {commentHistory.length === 0 ? (
            <Box 
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 100,
                mt: 3,
              }}
            >
              <Typography sx={{ textAlign: 'center', color: 'gray' }}>コメントはありません。</Typography>
            </Box> 
          ) : (
            <List >
              {sortedComments.map((Comment, index) => (
                <Box key={index}>
                  <ListItem sx={{ paddingBottom: '10px' }}>
                    <ListItemText
                      primary={`${Comment.user}\u00A0\u00A0\u00A0\u00A0${new Date(Comment.date).toLocaleString()}`}
                      secondary={<>
                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {Comment.value}
                        </Box>
                        {Comment.fileName && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            添付ファイル: {Comment.fileName}
                            <IconButton
                              onClick={() => handleFileDownload(Comment.fileId, Comment.fileName)}
                              sx={{ ml: 1 }}
                            >
                              <Download color="action" />
                            </IconButton>
                          </Box>
                        )}
                      </>
                      }
                    />
                  </ListItem>
                  {/* 区切り線を追加 */}
                  <Box sx={{ borderBottom: '1px solid #ddd' }} />
                </Box>
              ))}
            </List>
          )}
        </Box>
        </ResizableBox>
      </Box>
    </Box>
  );
};

export default Comment; 